import {
  Button,
  Col,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IPopupService } from "@shared/interfaces/popup-service.interface";
import { HttpErrorResponse } from "@shared/models/error/http-error-response";
import { PopupActionType } from "@shared/types/popup/enums/popup-action-types.enum";
import { PopupStatus } from "@shared/types/popup/enums/popup-statuses.enum";
import { PopupDto, UpdatePopupDto } from "@shared/types/popup/popup.dto";
import { ColumnsType } from "antd/es/table";
import { useMutation, UseMutationResult, useQuery } from "react-query";
import AlertModal from "./AlertModal/AlertModal";
import ChangePopupModal from "./ChangePopupModal/ChangePopupModal";
import styles from "./Popups.module.scss";
import { IPlanService } from "@shared/interfaces/plan-service.interface";
import { PlanStatus } from "@shared/types/plans/enum/plan-status.enum";

const ALL = "All";

const defaultPopup: Partial<PopupDto> = {
  name: "",
  description: "",
  data: null,
  actionOne: PopupActionType.CONTINUE,
  actionOneTitle: "",
  imageUrl: "",
  status: PopupStatus.ACTIVE,
  activeFrom: new Date(),
  activeTo: new Date(),
};

const ALL_OBJECT = { label: "All", value: ALL };

export const Statuses = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const Types = [
  {
    label: "Continue",
    value: PopupActionType.CONTINUE,
  },
  {
    label: "Draft Tokens",
    value: PopupActionType.DRAFT_TOKENS,
  },
  {
    label: "External link",
    value: PopupActionType.EXTERNAL_LINK,
  },
  {
    label: "In App Purchase",
    value: PopupActionType.IN_APP_PURCHASE,
  },
];

const STATUSES = [{ ...ALL_OBJECT, label: "Filter by status" }, ...Statuses];
const TYPES = [{ ...ALL_OBJECT, label: "Filter by type" }, ...Types];

const getStatus = (type: PopupStatus) => {
  switch (type) {
    case PopupStatus.ACTIVE:
      return <Tag color="#009E60">Active</Tag>;

    case PopupStatus.INACTIVE:
      return <Tag color="#f5222d">Inactive</Tag>;

    default:
      return null;
  }
};

const getPopupTypeTag = (type: PopupActionType) => {
  switch (type) {
    case PopupActionType.DRAFT_TOKENS:
      return <Tag color="#9400d3">Draft Tokens</Tag>;

    case PopupActionType.EXTERNAL_LINK:
      return <Tag color="#108ee9">External Link</Tag>;

    case PopupActionType.IN_APP_PURCHASE:
      return <Tag color="#aa6c39">In App Purchase</Tag>;

    default:
      return <Tag>Continue</Tag>;
  }
};

const Popups = () => {
  const [type, setType] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  const [page, setPage] = useState(1);
  const [currentPageSize, setPageSize] = useState(20);

  const [containerHeight, setContainerHeight] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState<PopupDto | null>(null);

  const plansService = appInject<IPlanService>(DI_TOKENS.plansService);
  const popupService = appInject<IPopupService>(DI_TOKENS.popupService);

  const columns = useMemo(
    () =>
      [
        {
          key: "id",
          dataIndex: "id",
          title: "ID",
          width: 300,
        },
        {
          key: "name",
          dataIndex: "name",
          title: "Name",
          width: 160,
        },
        // {
        //     key: "data",
        //     dataIndex: "data",
        //     title: "Data",
        //     width: 160,
        //     render: (value: Record<any, any>) => JSON.stringify(value),
        // },
        {
          key: "description",
          dataIndex: "description",
          title: "Description",
          render: (value: number | null) => (value === null ? "N/A" : value),
          width: 240,
        },
        {
          key: "status",
          dataIndex: "status",
          title: "Status",
          width: 100,
          render: getStatus,
        },
        {
          key: "actionOne",
          dataIndex: "actionOne",
          title: "Action One Type",
          width: 140,
          render: getPopupTypeTag,
        },
        {
          key: "actionTwo",
          dataIndex: "actionTwo",
          title: "Action Two Type",
          width: 140,
          render: getPopupTypeTag,
        },
        {
          key: "createdAt",
          dataIndex: "createdAt",
          title: "CreatedAt",
          render: (date: Date | null) =>
            date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
          width: 200,
        },
        {
          key: "action",
          title: "Actions",
          render: (_: null, popup: PopupDto) => {
            return (
              <Space
                size="middle"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="primary"
                  color="danger"
                  size="small"
                  style={{
                    outline: "none",
                  }}
                  onClick={() => {
                    setSelectedPopup(popup);
                    setIsOpenModal(true);
                  }}
                >
                  Edit
                </Button>
                {popup?.slug === null ? (
                  <Button
                    type="primary"
                    size="small"
                    style={{
                      backgroundColor: "#f5222d",
                      borderColor: "#f5222d",
                      outline: "none",
                    }}
                    onClick={() => {
                      setIsAlertModalOpen(true);
                      setSelectedPopup(popup);
                    }}
                  >
                    Delete
                  </Button>
                ) : null}
              </Space>
            );
          },
          width: 180,
        },
      ] as ColumnsType<PopupDto>,
    [],
  );

  const plansQuery = useQuery(
    ["download-plans-list"],
    () =>
      plansService.list({
        page,
        pageSize: currentPageSize,
        query: debouncedSearchText,
        status: PlanStatus.ACTIVE,
      }),
    {
      refetchOnMount: true,
    },
  );

  const popupQuery = useQuery(
    [
      "download-popups-list",
      page,
      currentPageSize,
      debouncedSearchText,
      status,
      type,
    ],
    () =>
      popupService.list({
        page,
        pageSize: currentPageSize,
        query: debouncedSearchText,
        status: (status === ALL
          ? undefined
          : (status ?? undefined)) as PopupStatus,
        type: type === ALL ? undefined : (type as PopupActionType),
      }),
    {
      refetchOnMount: true,
    },
  );

  const updatePopupMutation: UseMutationResult<
    PopupDto,
    unknown,
    UpdatePopupDto,
    unknown
  > = useMutation(
    (data: UpdatePopupDto): Promise<PopupDto> => popupService.update(data),
    {
      onSuccess: () => {
        setSelectedPopup(null);
        setIsOpenModal(false);
        popupQuery.refetch();
      },
    },
  );

  const createPopupMutation: UseMutationResult<
    PopupDto,
    unknown,
    PopupDto,
    unknown
  > = useMutation(
    (data: PopupDto): Promise<PopupDto> => popupService.create(data),
    {
      onSuccess: () => {
        setSelectedPopup(null);
        setIsOpenModal(false);
        popupQuery.refetch();
      },
    },
  );

  const deletePopupMutation: UseMutationResult<
    unknown,
    unknown,
    PopupDto,
    unknown
  > = useMutation((data: PopupDto): Promise<any> => popupService.delete(data), {
    onSuccess: () => {
      setSelectedPopup(null);
      setIsAlertModalOpen(false);
      popupQuery.refetch();
    },
  });

  const handleCloseDialog = () => {
    setIsOpenModal(false);
    setSelectedPopup(null);
    deletePopupMutation.reset();
  };

  const handleSaveDialog = (data: PopupDto) => {
    if (selectedPopup?.id) {
      updatePopupMutation.mutate({ ...data, id: selectedPopup.id });
    } else {
      createPopupMutation.mutate(data);
    }
  };

  const handleProceedDialog = () => {
    if (selectedPopup?.id) {
      deletePopupMutation.mutate(selectedPopup);
    }
  };

  const containerRef = useRef<any>();

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if ("offsetHeight" in containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight - 140);
      }
    }, 100);
  }, []);

  // Update debounced search text after a delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedSearchText(searchText);
    }, 300); // Adjust debounce delay as needed

    // Clear the timeout if the effect re-runs (when searchText changes)
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
  };

  const handleCloseAlertDialog = () => {
    setIsAlertModalOpen(false);
  };

  const getErrorMessage = (error: any) => {
    if (error) {
      if (error instanceof HttpErrorResponse) {
        return error.errors.get("message" as any) as unknown as string;
      }
      return `Unable to delete the popup. Please try again.`;
    }
  };

  const resetFilters = () => {
    setSearchText("");
    setStatus(ALL);
    setType(ALL);
  };

  return (
    <Layout className={styles.tableLayout}>
      {isOpenModal ? (
        <ChangePopupModal
          open={isOpenModal}
          popup={selectedPopup}
          onClose={handleCloseDialog}
          onSave={handleSaveDialog}
          isLoading={updatePopupMutation.isLoading}
          isError={updatePopupMutation.isError}
          plans={plansQuery.data?.data?.plans || []}
        />
      ) : (
        <></>
      )}

      {isAlertModalOpen ? (
        <AlertModal
          open={Boolean(isAlertModalOpen)}
          description={`Are you sure you want to delete the "${selectedPopup?.name}" popup?`}
          onClose={handleCloseAlertDialog}
          isLoading={deletePopupMutation.isLoading}
          error={getErrorMessage(deletePopupMutation.error)}
          onProceed={handleProceedDialog}
        />
      ) : (
        <></>
      )}

      <Row
        style={{
          height: 32,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Col
          span={7}
          style={{
            display: "flex",
            alignItems: "center",
            rowGap: 5,
            columnGap: 5,
            height: "100%",
          }}
        >
          <Typography.Title style={{ margin: 0 }} level={5}>
            Popups ({popupQuery.data?.data?.total})
          </Typography.Title>

          <Button
            type="primary"
            size="middle"
            onClick={() => {
              setSelectedPopup(defaultPopup as PopupDto);
              setIsOpenModal(true);
            }}
          >
            Add Popup
          </Button>
        </Col>
        <Col>
          <Form.Item className="search-input-container">
            <Input
              onChange={handleSearch}
              value={searchText}
              name="search"
              style={{
                minWidth: 200,
              }}
              placeholder="Search by Name"
            />

            <Select
              style={{
                minWidth: 150,
              }}
              options={STATUSES}
              value={status}
              onChange={setStatus}
            />

            <Select
              style={{ minWidth: 150 }}
              options={TYPES}
              value={type}
              onChange={setType}
            />

            <Button type="primary" onClick={resetFilters}>
              Reset
            </Button>
          </Form.Item>
        </Col>
      </Row>
      <Layout.Content
        ref={containerRef}
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Table
          rowKey="key"
          dataSource={popupQuery.data?.data?.popups || []}
          onChange={onTableChange}
          columns={columns}
          loading={popupQuery.isFetching}
          pagination={{
            current: page,
            total: popupQuery.data?.data?.total || 0,
            pageSize: currentPageSize,
            showSizeChanger: true,
          }}
          size="small"
          scroll={{ y: containerHeight, x: "max-content" }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Popups;
